import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Scissors, CheckCircle2, XCircle } from "lucide-react";
import PlatformLogo from "@/components/PlatformLogo";

/**
 * Landing page hit by the link inside the DOI verification email.
 * Calls the backend, marks the owner as verified, then offers a button to login.
 *
 * The backend `/public/register/verify/{token}` endpoint is idempotent — calling
 * it a second time with the same token returns `already_verified: true`, so we
 * do not need StrictMode-double-call guards on the client.
 */
export default function RegisterVerifyPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState({ status: "loading", email: null, err: null, already: false });

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/public/register/verify/${token}`);
        if (!alive) return;
        setState({ status: "ok", email: data.email || null, already: !!data.already_verified, err: null });
      } catch (e) {
        if (!alive) return;
        setState({ status: "err", err: formatApiError(e.response?.data?.detail) || "Bestätigungs-Link ungültig oder abgelaufen.", email: null, already: false });
      }
    })();
    return () => { alive = false; };
  }, [token]);

  return (
    <div className="min-h-screen flex flex-col" style={{ background: "var(--mv-bg)" }}>
      <header className="border-b" style={{ borderColor: "var(--mv-border)", background: "rgba(253,251,247,0.85)", backdropFilter: "blur(20px)" }}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <PlatformLogo imgClassName="h-7 w-auto object-contain">
              <Scissors className="w-5 h-5" strokeWidth={1.5} style={{ color: "var(--mv-primary)" }} />
              <span className="font-display text-lg">StudioMV</span>
            </PlatformLogo>
          </Link>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="mv-card p-10 max-w-md w-full text-center" data-testid="register-verify-card">
          {state.status === "loading" && (
            <p className="text-sm" style={{ color: "var(--mv-text-muted)" }}>E-Mail wird bestätigt …</p>
          )}
          {state.status === "ok" && (
            <>
              <CheckCircle2 className="w-16 h-16 mx-auto mb-4" style={{ color: "var(--mv-primary)" }} strokeWidth={1.5} />
              <h1 className="font-display text-3xl mb-3">
                {state.already ? "Bereits bestätigt" : "Konto aktiviert"}
              </h1>
              <p className="text-base mb-8" style={{ color: "var(--mv-text-muted)" }}>
                {state.already
                  ? "Dein Konto ist bereits aktiviert. Du kannst dich direkt einloggen."
                  : "Deine E-Mail-Adresse wurde bestätigt. Du kannst dich jetzt einloggen und mit der Einrichtung beginnen."}
              </p>
              <button
                type="button"
                onClick={() => navigate("/login")}
                className="mv-btn-primary w-full"
                data-testid="verify-to-login"
              >
                Zum Login
              </button>
            </>
          )}
          {state.status === "err" && (
            <>
              <XCircle className="w-16 h-16 mx-auto mb-4 text-red-500" strokeWidth={1.5} />
              <h1 className="font-display text-3xl mb-3">Link ungültig</h1>
              <p className="text-base mb-8" style={{ color: "var(--mv-text-muted)" }}>
                {state.err}
              </p>
              <Link to="/login" className="mv-btn-primary w-full inline-block" data-testid="verify-to-login-err">
                Zum Login
              </Link>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
